/**
 * Las tres escrituras del caso: asignar, mover de estado y anotar.
 *
 * Vive en un hook y no dentro de la vista porque el mismo caso se opera desde
 * dos lugares —los botones del panel y los atajos de teclado— y dos copias de
 * la misma mutación son dos estados de "guardando" que se contradicen en
 * pantalla.
 *
 * Nada de esto recarga la pantalla: al terminar se invalida la cache de
 * alertas y TanStack Query vuelve a pedir sólo lo que cambió. Un investigador
 * que tiene que apretar F5 después de cerrar un caso pierde la posición en la
 * bandeja, que es lo que estaba usando para saber por dónde iba.
 *
 * Las mutaciones **no se reintentan solas** (está en los defaults del
 * `queryClient`): reintentar un "confirmar falsificación" que en realidad sí
 * se aplicó lo registraría dos veces en la auditoría.
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import type { ApiError } from '@/api/errors';
import { alertKeys } from '@/api/keys';
import type { components } from '@/api/schema';
import { useApi } from '@/api/useApi';
import type { AlertStatus } from './severity';

type CaseState = components['schemas']['CaseStateOut'];
type Note = components['schemas']['NoteOut'];

export interface StatusChange {
  status: AlertStatus;
  /** Obligatorio al confirmar o descartar; prohibido en el resto. */
  resolution: string | null;
  /** Nota que queda junto al cambio de estado. */
  note: string | null;
}

export interface CaseActions {
  assign: UseMutationResult<CaseState, ApiError, number | null>;
  changeStatus: UseMutationResult<CaseState, ApiError, StatusChange>;
  addNote: UseMutationResult<Note, ApiError, string>;
  isPending: boolean;
  error: ApiError | null;
}

export function useCaseActions(alertId: number | null): CaseActions {
  const api = useApi();
  const queryClient = useQueryClient();

  // Invalidar la raíz alcanza a la lista, al detalle, a las notas, al
  // historial y a los contadores de la cabecera: cerrar un caso cambia los
  // cinco a la vez y refrescar sólo el detalle dejaría el contador mintiendo.
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: alertKeys.all });
  };

  const assign = useMutation<CaseState, ApiError, number | null>({
    mutationFn: (userId) =>
      api.post<CaseState>(`/api/v1/alerts/${alertId}/assign`, { user_id: userId }),
    onSuccess: refresh,
  });

  const changeStatus = useMutation<CaseState, ApiError, StatusChange>({
    mutationFn: (change) =>
      api.post<CaseState>(`/api/v1/alerts/${alertId}/status`, {
        status: change.status,
        resolution: change.resolution,
        note: change.note,
      }),
    onSuccess: refresh,
  });

  const addNote = useMutation<Note, ApiError, string>({
    mutationFn: (body) => api.post<Note>(`/api/v1/alerts/${alertId}/notes`, { body }),
    onSuccess: refresh,
  });

  return {
    assign,
    changeStatus,
    addNote,
    isPending: assign.isPending || changeStatus.isPending || addNote.isPending,
    error: assign.error ?? changeStatus.error ?? addNote.error,
  };
}
